import { LocationItem } from '../../../types/search';
import { fetchUniversalSearch, getProjectNameData } from '../../../utils/requests';
import { getProvinceData, loadLocationData } from '../utils';

interface CategoryItem {
  category_ID: string;
  category_Name_TH: string;
  category_Name_EN: string;
  category_Name_CN: string;
}

interface ActionItem {
  id: string;
  action: string;
}

export class SearchService {
  static getActionIdFromType(type: string, filteredActions: ActionItem[], actionMapping: Record<string, string>): number | null {
    const actionByValue = filteredActions.find(action => 
      action.action === type
    );
    
    if (actionByValue) {
      return Number(actionByValue.id);
    }
    
    const actionId = Object.keys(actionMapping).find(id => 
      actionMapping[id] === type
    );
    
    return actionId ? Number(actionId) : null;
  }

  static getCategoryIdFromType(type: string, t: (key: string) => string, filteredCategories: CategoryItem[], categoryMapping: Record<string, string>, locale: string): number | null {
    if (type === t('all')) return null;

    const categoryByName = filteredCategories.find(category => {
      if (locale === 'en') return category.category_Name_EN === type;
      if (locale === 'cn') return category.category_Name_CN === type;
      return category.category_Name_TH === type;
    });
    
    if (categoryByName) {
      return Number(categoryByName.category_ID);
    }
    
    const categoryId = Object.keys(categoryMapping).find(id => 
      categoryMapping[id] === type
    );
    
    return categoryId ? Number(categoryId) : null;
  }

  static async checkCachedResults(searchValue: string, locale: string, setIsSearching: (loading: boolean) => void, setSearchResults: (results: LocationItem[]) => void, setHasMore: (hasMore: boolean) => void, setIsLoadingMore: (loading: boolean) => void): Promise<boolean> {
    try {
      setIsSearching(true);

      const result = await fetchUniversalSearch(searchValue, locale, 1, 10);

      if (result.results.length > 0) {
        setSearchResults(result.results);
        setHasMore(result.total > result.results.length);
        setIsSearching(false);
        setIsLoadingMore(false);
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }

  static async performUniversalSearch(searchValue: string, locale: string, page: number = 1, pageSize: number = 10): Promise<{ results: LocationItem[], total: number }> {
    try {
      const result = await fetchUniversalSearch(searchValue, locale, page, pageSize);
      return result;
    } catch (error) {
      return { results: [], total: 0 };
    }
  }

  static async loadLocationDataBySearch(
    typeId: number, 
    searchValue: string, 
    transactionType: string, 
    propertyType: string, 
    locale: string,
    getActionIdFromType: (type: string) => number | null,
    getCategoryIdFromType: (type: string) => number | null
  ): Promise<{ data: LocationItem[], hasMore: boolean }> {
    try {
      let locations: LocationItem[] = [];
      let hasMore = false;

      if (typeId === -1) {
        const selectedActionId = getActionIdFromType(transactionType);
        const selectedCategoryId = getCategoryIdFromType(propertyType);

        const projectsResult = await getProjectNameData(
          selectedActionId,
          selectedCategoryId,
          searchValue,
          locale,
          1,
          20
        );

        const projects = projectsResult.data || [];
        locations = projects.map(p => ({ ...p, typeId: -1 }));
        hasMore = projectsResult.hasMore;
      } else if (typeId === 0) {
        locations = await getProvinceData(locale, searchValue, false);
        hasMore = false;
      } else {
        locations = await loadLocationData(typeId, locale, searchValue);
        hasMore = false;
      }

      return { data: locations, hasMore };
    } catch (error) {
      return { data: [], hasMore: false };
    }
  }

  static async loadMoreSearchResults(
    searchText: string, 
    locale: string, 
    page: number, 
    searchResults: LocationItem[]
  ): Promise<{ results: LocationItem[], hasMore: boolean, newPage: number }> {
    try {
      const result = await fetchUniversalSearch(searchText, locale, page + 1, 10);

      const combinedResults = [...searchResults, ...result.results];

      const hasMore = result.total > combinedResults.length && result.results.length >= 10;

      return {
        results: combinedResults,
        hasMore,
        newPage: page + 1
      };
    } catch (error) {
      return {
        results: searchResults,
        hasMore: false,
        newPage: page
      };
    }
  }

  static async loadMoreProjectData(
    modalSearchValue: string,
    transactionType: string,
    propertyType: string,
    locale: string,
    page: number,
    locationData: LocationItem[],
    getActionIdFromType: (type: string) => number | null,
    getCategoryIdFromType: (type: string) => number | null
  ): Promise<{ data: LocationItem[], hasMore: boolean, newPage: number }> {
    try {
      const selectedActionId = getActionIdFromType(transactionType);
      const selectedCategoryId = getCategoryIdFromType(propertyType);

      const projectsResult = await getProjectNameData(
        selectedActionId,
        selectedCategoryId,
        modalSearchValue,
        locale,
        page + 1,
        20
      );

      const combinedData = [...locationData, ...projectsResult.data];

      return {
        data: combinedData,
        hasMore: projectsResult.hasMore,
        newPage: page + 1
      };
    } catch (error) {
      return {
        data: locationData,
        hasMore: false,
        newPage: page
      };
    }
  }
} 